import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { DataserviceService } from 'src/app/dataservice.service';
import { formatDate } from '@angular/common';
import { NzMessageService } from 'ng-zorro-antd/message';

export function adultValidator(control: AbstractControl): ValidationErrors | null {
  if (!control.value) return null;

  const dob = new Date(control.value);
  const today = new Date();

  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age--;
  }

  return age < 18 ? { notAdult: true } : null;
}

export function zipValidator(control: AbstractControl): ValidationErrors | null {
  if (!control.value) return null;
  const valid = /^\d{5}$/.test(control.value);
  return valid ? null : { invalidZip: true };
}

const disallowSelfPhone = (own: string) => {
  const norm = (v: string) => {
    const d = (v || '').replace(/\D/g, '');
    return d.length === 11 && d.startsWith('1') ? d.slice(1) : d;
  };
  const ownNorm = norm(own);
  return (c: AbstractControl): ValidationErrors | null =>
    norm(c.value) === ownNorm ? { selfPhone: true } : null;
};

@Component({
  selector: 'app-starlink-general',
  templateUrl: './starlink-general.component.html',
  styleUrl: './starlink-general.component.css'
})
export class StarlinkGeneralComponent implements OnInit {
  @Input() fullData: any;
  @Output() customerData = new EventEmitter<{ customerInfo: any; nextPage: string; customerId?: string, userId: string, companyId: string, distributorId: string, provider: string, provider_id: string, workOrderId: any }>();

  infoForm: FormGroup;
  currentUser: any;
  states: any;
  btnLoading: boolean = false;
  filteredProviders: any;

  constructor(
    private fb: FormBuilder,
    private data: DataserviceService,
    private message: NzMessageService
  ) {
    this.infoForm = this.fb.group({
      firstName: [null, Validators.required],
      lastName: [null, Validators.required],
      address: [null, Validators.required],
      phone: [null, Validators.required],
      email: [null, [Validators.required, Validators.email]],
      permissionToCall: [null, Validators.requiredTrue],
      dob: ['', [Validators.required, adultValidator]],
      city: [null, Validators.required],
      state: [null, Validators.required],
      zip: [null, [Validators.required, zipValidator]],
    });
    this.states = this.data.states;

    this.currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['fullData'] && this.fullData?.customerInfo) {
      this.patchFormFromFullData();
    }
  }

  ngOnInit() {
    // console.log('Full data: ', this.fullData);
    this.filteredProviders = this.currentUser.providers.filter(
      (provider: any) => provider.name.toLowerCase() === 'starlink'
    );

    // console.log('Current user: ', this.currentUser);
    // console.log('Current user providers: ', this.currentUser.providers);
    // console.log('Filtered providers: ', this.filteredProviders);

    if (this.fullData?.customerInfo) {
      this.patchFormFromFullData();
    }
    this.applyPhoneValidator();
  }

  private patchFormFromFullData(): void {
    const info = this.fullData.customerInfo;
    this.infoForm.patchValue({
      firstName: info.firstName ?? null,
      lastName: info.lastName ?? null,
      address: info.address ?? null,
      phone: info.phone ?? null,
      email: info.email ?? null,
      permissionToCall: info.permissionToCall ?? null,
      // dob: info.dob ?? '',
      city: info.city ?? null,
      state: info.state ?? null,
      zip: info.zip ?? null,
    });
  }

  applyPhoneValidator() {
    const phoneCtrl = this.infoForm.get('phone');
    if (phoneCtrl && this.currentUser?.phone) {
      phoneCtrl.setValidators([Validators.required, disallowSelfPhone(this.currentUser.phone)]);
      phoneCtrl.updateValueAndValidity();
    }
  }

  submitForm() {
    const rawValues = this.infoForm.getRawValue();
    if (rawValues.dob) {
      rawValues.dobStr = formatDate(rawValues.dob, 'MM/dd/yyyy', 'en-US');
    }

    const obj = {
      ...(this.fullData?.replayedLead && this.fullData?.customerId
        ? { id: this.fullData.customerId }
        : {}),
      user_id: this.currentUser?.id,
      company_id: this.currentUser?.company_id,
      distributor_id: this.currentUser?.distributor_id ?? null,
      first_name: rawValues.firstName,
      last_name: rawValues.lastName,
      address_line_1: rawValues.address,
      address_city: rawValues.city,
      address_state: rawValues.state,
      address_zip: rawValues.zip,
      phone_number: rawValues.phone,
      email: rawValues.email,
      date_of_birth: rawValues.dobStr,
      origination: 'starlink-flow'
    };

    // console.log('OBJ: ', obj);

    const formValues = { ...rawValues };
    this.btnLoading = true;
    const loadingId = this.message.loading('Creating lead...', { nzDuration: 0 }).messageId;

    if (this.fullData?.replayedLead) {
      obj['id'] = this.fullData.customerId;
      this.data.postDataNoBodyDotBody(obj, 'customers/update').subscribe(
        (r: any) => {
          this.message.remove(loadingId);
          this.btnLoading = false;
          if (r?.result === 'success') {
            this.customerData.emit({
              customerInfo: formValues,
              nextPage: 'catalog',
              customerId: this.fullData.customerId,
              userId: this.currentUser?.id,
              distributorId: this.currentUser?.distributor_id,
              companyId: this.currentUser?.company_id,
              provider: this.filteredProviders?.[0]?.name || 'starlink',
              provider_id: this.filteredProviders?.[0]?.id,
              workOrderId: this.fullData.workOrderId ? this.fullData.workOrderId : null
            });
          } else {
            alert(r?.message || 'Failed to update lead. Please try again.');
          }
        },
        (e) => {
          this.message.remove(loadingId);
          this.btnLoading = false;
          console.error('Starlink create lead error:', e);
          alert('Failed to create lead. Please try again.');
        }
      );
    } else {
      this.data.postDataNoBodyDotBody(obj, 'customers/create').subscribe(
        (r: any) => {
          this.message.remove(loadingId);
          this.btnLoading = false;
          if (r?.result === 'success' && r?.data?.id) {
  
            // console.log('R.DATA CREATE: ', r?.data);
            this.customerData.emit({
              customerInfo: formValues,
              nextPage: 'catalog',
              customerId: r.data.id,
              userId: this.currentUser?.id,
              distributorId: this.currentUser?.distributor_id,
              companyId: this.currentUser?.company_id,
              provider: this.filteredProviders?.[0]?.name || 'starlink',
              provider_id: this.filteredProviders?.[0]?.id,
              workOrderId: null
            });
          } else {
            alert(r?.message || 'Failed to create lead. Please try again.');
          }
        },
        (e) => {
          this.message.remove(loadingId);
          this.btnLoading = false;
          console.error('Starlink create lead error:', e);
          alert('Failed to create lead. Please try again.');
        }
      );
    }

    // this.data.postDataNoBodyDotBody(obj, 'customers/create').subscribe(
    //   (r: any) => {
    //     this.message.remove(loadingId);
    //     this.btnLoading = false;
    //     if (r?.result === 'success' && r?.data?.id) {

    //       console.log('R.DATA: ', r?.data);
    //       this.customerData.emit({
    //         customerInfo: formValues,
    //         nextPage: 'catalog',
    //         customerId: r.data.id,
    //         userId: this.currentUser?.id,
    //         distributorId: this.currentUser?.distributor_id,
    //         companyId: this.currentUser?.company_id,
    //         provider: this.filteredProviders?.[0]?.name || 'starlink',
    //         provider_id: this.filteredProviders?.[0]?.id
    //       });
    //     } else {
    //       alert(r?.message || 'Failed to create lead. Please try again.');
    //     }
    //   },
    //   (e) => {
    //     this.message.remove(loadingId);
    //     this.btnLoading = false;
    //     console.error('Starlink create lead error:', e);
    //     alert('Failed to create lead. Please try again.');
    //   }
    // );
  }

}
